import * as React from "react";
import { motionMs } from "./tokens";

/** Cross-fades run only with --motion-base above 0 (reduced motion reads 0) and CSS animations (not jsdom). */
export const crossFadeOn = (el: HTMLElement | null): el is HTMLElement =>
  !!el && typeof el.getAnimations === "function" && motionMs("base") > 0;

/**
 * A copy of `el` as it is painted: every [data-pending] node (the root included) loses the attribute and keeps
 * its live opacity inline, so a ghost of a dimmed body leaves at the dim it showed (Swap, 11-01).
 */
export const snapshotNode = (el: HTMLElement): HTMLElement => {
  const g = el.cloneNode(true) as HTMLElement;
  const live = [el, ...el.querySelectorAll<HTMLElement>("[data-pending]")];
  const copy = [g, ...g.querySelectorAll<HTMLElement>("[data-pending]")];
  live.forEach((n, i) => {
    if (!n.hasAttribute("data-pending")) return;
    const c = copy[i];
    c.removeAttribute("data-pending");
    c.style.opacity = getComputedStyle(n).opacity;
    c.style.transition = "none";
  });
  return g;
};

/** Lays ghost `g` over `host` at `top`/`height` and drops it when its fade ends. */
export const mountGhost = (
  g: HTMLElement,
  host: HTMLElement,
  at: { top: number; height: number; scrollTop: number },
): void => {
  g.classList.add("motion-ghost");
  g.setAttribute("aria-hidden", "true");
  g.setAttribute("inert", "");
  g.removeAttribute("data-testid");
  g.querySelectorAll("[id],[data-testid]").forEach((n) => {
    n.removeAttribute("id");
    n.removeAttribute("data-testid");
  });
  Object.assign(g.style, { top: `${at.top}px`, height: `${at.height}px` });
  host.appendChild(g);
  g.scrollTop = at.scrollTop;
  const drop = () => g.remove();
  g.addEventListener("animationend", drop);
  // Fallback when animationend never fires (tab hidden mid-fade).
  window.setTimeout(drop, motionMs("base") * 2);
};

/**
 * Cross-fade on `key` change: a copy of the last body fades out over the new one
 * (R14 rail). Off under reduced motion and without CSS animations (jsdom). `host` must be the positioned parent
 * of `body`.
 */
export const useCrossFade = (
  body: React.RefObject<HTMLElement>,
  host: React.RefObject<HTMLElement>,
  key: string,
) => {
  const last = React.useRef<{
    key: string;
    node: HTMLElement;
    st: number;
  } | null>(null);
  React.useLayoutEffect(() => {
    const prev = last.current;
    const el = body.current;
    if (!prev || prev.key === key || !crossFadeOn(el) || !host.current) return;
    mountGhost(prev.node, host.current, {
      top: el.offsetTop,
      height: el.offsetHeight,
      scrollTop: prev.st,
    });
  }, [key, body, host]);
  // After every commit, keep a copy of what is on screen for the next change.
  React.useLayoutEffect(() => {
    if (body.current)
      last.current = {
        key,
        node: body.current.cloneNode(true) as HTMLElement,
        st: body.current.scrollTop,
      };
  });
};
